// @flow

import {
  type UserInputValidationError,
  type UserInputValidatorArgs,
  onlyWhenSupport,
  VALID,
} from '@tectonics/prs/validation';

import { validateColombianCity } from './colombian-city';
import { validateColombianState } from './colombian-state';
import { validateColombianZipcode } from './colombian-zipcode';
import { validateColombianAddress } from './colombian-address';
import { validateColombianDocument } from './colombian-document';
import { validateColombianPhoneNumber, validateColombianNequiPhoneNumber } from './colombian-phone-number';
import { validateColombianStreetNumber } from './colombian-street-number';
import { validateColombianDocumentType } from './colombian-document-type';
import { validateColombianExternalPayProvider } from './colombian-externalpayprovider';
import { validateColombianExternalPayProviderBank } from './colombian-externalpayprovider-bank';
import { getCurrentPaymentMethodOptionalFields, hasOptionalField } from '@tectonics/prs/incoming-payment/get-payment-type-optional-fields';
import { isNequiPayment } from '@tectonics/prs/colombia/constants';

import { getCurrentFeatures } from '@tectonics/prs/payment-spec';

function validateUnlessOptional(
  optionalFields: ?Array<string>,
  optionalFieldName: string,
  onlyWhen: (feature: string) => any,
  featureName: string,
  validateFn: () => ?UserInputValidationError,
): ?UserInputValidationError {
  if (hasOptionalField(optionalFields, optionalFieldName)) {
    return null;
  }
  return onlyWhen(featureName) && validateFn();
}

export function validator({
  spec,
  prsSupport,
  userInputName,
  userInputValue,
  userInputValues,
  incomingPayment,
}: UserInputValidatorArgs): ?UserInputValidationError {
  const currentFeatures = getCurrentFeatures(spec, userInputValues['selectedPaymentMethod']);
  const onlyWhen = onlyWhenSupport(currentFeatures, prsSupport);
  let optionalFields = incomingPayment.status === 'OP' ? getCurrentPaymentMethodOptionalFields(incomingPayment, userInputValues, spec) : [];

  switch (userInputName) {
    case 'colombianDocument':
      return validateUnlessOptional(optionalFields, 'document', onlyWhen, 'field_colombian_document',
        () => validateColombianDocument(userInputValue, userInputValues['colombianDocumentType']));

    case 'colombianDocumentType':
      return validateUnlessOptional(optionalFields, 'document', onlyWhen, 'field_colombian_document_type',
        () => validateColombianDocumentType(userInputValue));

    case 'colombianPhoneNumber': {
      const isNequi = isNequiPayment(userInputValues);
      const phoneValidator = isNequi ? validateColombianNequiPhoneNumber : validateColombianPhoneNumber;
      return validateUnlessOptional(optionalFields, 'telephone', onlyWhen, 'field_colombian_phone_number',
        () => phoneValidator(userInputValue));
    }

    case 'colombianZipcode':
      return validateUnlessOptional(optionalFields, 'zipcode', onlyWhen, 'field_colombian_zipcode',
        () => validateColombianZipcode(userInputValue));

    case 'colombianState':
      return validateUnlessOptional(optionalFields, 'state', onlyWhen, 'field_colombian_state',
        () => validateColombianState(userInputValue));

    case 'colombianCity':
      return validateUnlessOptional(optionalFields, 'city', onlyWhen, 'field_colombian_city',
        () => validateColombianCity(userInputValue));

    case 'colombianAddress':
      return validateUnlessOptional(optionalFields, 'address', onlyWhen, 'field_colombian_address',
        () => validateColombianAddress(userInputValue));

    case 'colombianStreetNumber':
      return validateUnlessOptional(optionalFields, 'street_number', onlyWhen, 'field_colombian_street_number',
        () => validateColombianStreetNumber(userInputValue));

    case 'selectedExternalPayProvider':
      return onlyWhen('field_colombian_externalpayprovider_code') && validateColombianExternalPayProvider(userInputValue);

    case 'selectedExternalPayProviderBank':
      return onlyWhen('field_colombian_externalpayprovider_code') && validateColombianExternalPayProviderBank(userInputValue, userInputValues['selectedExternalPayProvider']);

    default:
      return VALID;
  }
}
